// In-game crosshair overlay: owns the canvas, the animator and the redraw policy.

import { CrosshairAnimator } from "./animate";
import { CrosshairRenderer } from "./render";
import type { CrosshairConfig, Trigger } from "./types";
import { hudStageRect, hudStageSize } from "../stage";

export class CrosshairHud {
  private renderer: CrosshairRenderer;
  private animator: CrosshairAnimator;
  private cfg: CrosshairConfig;
  private zoomCfg: CrosshairConfig | null = null;
  private zoomed = false;
  private visible = true;
  private dirty = true;
  private layoutDirty = true;
  private activeCfg: CrosshairConfig;
  private lastW = 0;
  private lastH = 0;
  private lastDpr = 0;

  constructor(
    private canvas: HTMLCanvasElement,
    cfg: CrosshairConfig,
  ) {
    this.cfg = cfg;
    this.activeCfg = cfg;
    this.renderer = new CrosshairRenderer(canvas, cfg);
    this.animator = new CrosshairAnimator(cfg);
    this.place();
  }

  setConfig(cfg: CrosshairConfig): void {
    this.cfg = cfg;
    this.dirty = true;
    this.layoutDirty = true;
  }

  setZoomConfig(cfg: CrosshairConfig | null): void {
    this.zoomCfg = cfg;
    this.dirty = true;
    this.layoutDirty = true;
  }

  setZoomed(zoomed: boolean): void {
    if (this.zoomed === zoomed) return;
    this.zoomed = zoomed;
    this.dirty = true;
  }

  setVisible(visible: boolean): void {
    if (this.visible === visible) return;
    this.visible = visible;
    this.canvas.style.display = visible ? "block" : "none";
    this.dirty = true;
  }

  fire(kind: Trigger, now: number, count = 1): void {
    this.animator.fire(kind, now, count);
  }

  private place(): void {
    const css = this.renderer.cssSide;
    const stage = hudStageRect();
    const left = Math.round(stage.x + (stage.w - css) / 2);
    const top = Math.round(stage.y + (stage.h - css) / 2);
    this.canvas.style.left = `${left}px`;
    this.canvas.style.top = `${top}px`;
    this.canvas.style.width = `${css}px`;
    this.canvas.style.height = `${css}px`;
    this.lastW = stage.w;
    this.lastH = stage.h;
    this.lastDpr = window.devicePixelRatio || 1;
  }

  /** Render if needed. Safe to call every frame. */
  tick(now: number): void {
    if (!this.visible) return;
    const dpr = window.devicePixelRatio || 1;
    const stage = hudStageSize();
    if (
      this.layoutDirty ||
      stage.w !== this.lastW ||
      stage.h !== this.lastH ||
      dpr !== this.lastDpr
    ) {
      this.renderer.layout(dpr);
      this.place();
      this.layoutDirty = false;
      this.dirty = true;
    }

    const cfg = this.zoomed && this.zoomCfg ? this.zoomCfg : this.cfg;
    if (cfg !== this.activeCfg) {
      this.activeCfg = cfg;
      this.animator.setConfig(cfg);
      this.renderer.setConfig(cfg);
      this.renderer.layout();
      this.place();
      this.dirty = true;
    }

    const active = this.animator.active(now);
    if (!this.dirty && !active) return;
    this.renderer.render(this.animator.sample(now));
    this.dirty = false;
  }
}
